import { toPng } from 'html-to-image'
import { sampleAt, type LapTrace } from '../analysis/laps'
import { CORNER_LEAN_DEG, sectionStats, type Section, type StatChannels } from '../analysis/sections'

/** Everything the export needs to describe the comparison in words and numbers. */
export interface ReportContext {
  track?: string
  date?: Date
  traces: LapTrace[]
  refTrace: LapTrace
  sections: Section[]
  channels: StatChannels & { speedUnit?: string }
  /** The sessions the compared laps come from, for attaching the original files */
  sessions: { id: string; name: string }[]
}

export interface ExportFile {
  name: string
  blob: Blob
}

/** File name stem: track, date and the laps compared, safe for any file system. */
export function reportStem(ctx: ReportContext): string {
  const d = ctx.date ?? new Date()
  const day = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`
  const laps = ctx.traces.map((t) => t.label.replace(/^sample /, '')).join('_vs_')
  return `mala-racing_${ctx.track ?? 'track'}_${day}_${laps}`.replace(/[^\w.-]+/g, '_').slice(0, 120)
}

/** The message to paste into an AI assistant: the ask, the context and the numbers. */
export function buildPrompt(ctx: ReportContext, include: { image: boolean; csv: boolean; sessions?: boolean }): string {
  const { traces, refTrace, sections, channels } = ctx
  const unit = channels.speedUnit ?? 'mph'
  const length = refTrace.distance[refTrace.distance.length - 1]
  const others = traces.filter((t) => t !== refTrace)
  const lines: string[] = []

  lines.push(
    'You are an experienced riding coach. I ride a motorcycle on a kart track and logged these laps with an AiM Solo 2 GPS lap timer. ' +
      'Compare each lap with the reference lap section by section: where is time lost or gained, and what explains it in the speed and lean data ' +
      '(braking point, minimum speed and where the slowest point sits, how early the drive out starts) and in the racing lines on the map ' +
      '(does the line use the full width of the track, where are turn-in, apex and the point where acceleration starts, and how do the lines differ where the time differs). ' +
      'I read this between sessions with little time: be concise — short bullet points per section, no preamble, no repetition of the numbers I gave you, ' +
      'refer to sections by name only. Finish with the three changes that would gain the most time.',
    '',
    '## Context',
    `- Track: ${ctx.track ?? 'unknown'}; lap length ${Math.round(length)} m. Distances are metres from the start/finish line along the reference lap.`,
    `- Reference lap: ${refTrace.label} — ${fmtLap(refTrace.lap.duration)} (the fastest of the selected laps).`,
  )
  if (others.length)
    lines.push(
      `- Compared laps: ${others.map((t) => `${t.label} — ${fmtLap(t.lap.duration)} (${signed(t.lap.duration - refTrace.lap.duration, 3)} s)`).join('; ')}.`,
    )
  if (sections.length)
    lines.push(
      `- Sections: ${sections.map((s) => `${s.name} ${Math.round(s.from)}–${Math.round(s.to)} m`).join('; ')}.`,
    )
  lines.push(
    `- Speeds in ${unit}. Lean angle is the combined lean estimated from GPS (lateral acceleration), + is right, − is left; it is not measured by a sensor, so treat it as a trend rather than an exact value.`,
  )

  const all: Section[] = [...sections, { index: sections.length, name: 'Whole lap', from: 0, to: length }]
  const stats = new Map(traces.map((t) => [t.key, sectionStats(t, all, channels)]))
  const ref = stats.get(refTrace.key)!
  const hasSpeed = !!channels.speed
  const hasLean = !!channels.lean

  for (const t of others) {
    const st = stats.get(t.key)!
    lines.push('', `## ${t.label} vs reference ${refTrace.label} (reference → lap)`)
    const head = ['Section', 'Time (ref → lap)', 'Δ time']
    if (hasSpeed) head.push(`Entry speed (${unit})`, `Min speed (${unit})`, 'Slowest point (m)', `Exit speed (${unit})`)
    if (hasLean) head.push('Max lean left (°)', 'Max lean right (°)')
    lines.push(`| ${head.join(' | ')} |`, `| ${head.map(() => '---').join(' | ')} |`)
    st.forEach((s, i) => {
      const r = ref[i]
      const row = [
        i === sections.length ? 'Whole lap' : s.section.name,
        `${r.time.toFixed(3)} → ${s.time.toFixed(3)} s`,
        `${signed(s.time - r.time, 3)} s`,
      ]
      if (hasSpeed)
        row.push(
          pair(r.entrySpeed, s.entrySpeed, 1),
          pair(r.minSpeed, s.minSpeed, 1),
          pair(r.minSpeedAt, s.minSpeedAt, 0),
          pair(r.exitSpeed, s.exitSpeed, 1),
        )
      if (hasLean)
        row.push(
          r.leanLeft >= CORNER_LEAN_DEG || s.leanLeft >= CORNER_LEAN_DEG ? pair(r.leanLeft, s.leanLeft, 0) : '–',
          r.leanRight >= CORNER_LEAN_DEG || s.leanRight >= CORNER_LEAN_DEG ? pair(r.leanRight, s.leanRight, 0) : '–',
        )
      lines.push(`| ${row.join(' | ')} |`)
    })
  }
  if (!others.length) {
    lines.push('', `## Reference lap ${refTrace.label} by section`)
    const head = ['Section', 'Time (s)']
    if (hasSpeed) head.push(`Entry speed (${unit})`, `Min speed (${unit})`, 'Slowest point (m)', `Exit speed (${unit})`)
    if (hasLean) head.push('Max lean left (°)', 'Max lean right (°)')
    lines.push(`| ${head.join(' | ')} |`, `| ${head.map(() => '---').join(' | ')} |`)
    ref.forEach((s, i) => {
      const row = [i === sections.length ? 'Whole lap' : s.section.name, s.time.toFixed(3)]
      if (hasSpeed) row.push(num(s.entrySpeed, 1), num(s.minSpeed, 1), num(s.minSpeedAt, 0), num(s.exitSpeed, 1))
      if (hasLean) row.push(num(s.leanLeft, 0), num(s.leanRight, 0))
      lines.push(`| ${row.join(' | ')} |`)
    })
  }

  const attached: string[] = []
  if (include.image)
    attached.push('an image of the summary, the diagrams (with the section boundaries drawn) and the racing lines')
  if (include.csv)
    attached.push(
      `a CSV with speed, time difference to the reference and lean angle every ${CSV_STEP_M} m for each lap`,
    )
  if (include.sessions && ctx.sessions.length)
    attached.push(
      `the original AiM Solo 2 session file${ctx.sessions.length > 1 ? 's' : ''} (${ctx.sessions.map((s) => s.name.replace(/^sample /, '')).join(', ')}) in case you can read them`,
    )
  if (attached.length) lines.push('', `Attached: ${attached.join(', and ')}.`)
  return lines.join('\n')
}

export const CSV_STEP_M = 5

/** Speed, Δ time and lean for every lap, one row every CSV_STEP_M metres. */
export function buildCsv(ctx: ReportContext): string {
  const { traces, refTrace, channels, sections } = ctx
  const unit = channels.speedUnit ?? 'mph'
  const length = Math.min(...traces.map((t) => t.distance[t.distance.length - 1]))
  const head = ['distance_m', 'section']
  for (const t of traces) {
    head.push(`${t.label} speed_${unit}`)
    if (t !== refTrace) head.push(`${t.label} delta_s`)
    if (channels.lean) head.push(`${t.label} lean_deg`)
  }
  const rows = [head.map(csvCell).join(',')]
  for (let d = 0; d <= length; d += CSV_STEP_M) {
    const sec = sections.find((s) => d >= s.from && (d < s.to || s.index === sections.length - 1))
    const row = [String(d), sec?.name ?? '']
    const tRef = sampleAt(refTrace.distance, refTrace.time, d)
    for (const t of traces) {
      const speed = channels.speed ? t.channels[channels.speed] : undefined
      row.push(speed ? sampleAt(t.distance, speed, d).toFixed(1) : '')
      if (t !== refTrace) row.push((sampleAt(t.distance, t.time, d) - tRef).toFixed(3))
      if (channels.lean) {
        const lean = t.channels[channels.lean]
        row.push(lean ? sampleAt(t.distance, lean, d).toFixed(1) : '')
      }
    }
    rows.push(row.map(csvCell).join(','))
  }
  return rows.join('\n') + '\n'
}

/**
 * Rasterize the hidden report into a PNG. WebKit sometimes returns a blank image on the first
 * pass (fonts and canvases not yet in the clone), so the result is taken from a second pass.
 */
export async function renderReportPng(el: HTMLElement): Promise<Blob> {
  const opts = { pixelRatio: 2, backgroundColor: '#171a21', cacheBust: false }
  const webkit = /AppleWebKit/.test(navigator.userAgent) && !/Chrome|Chromium/.test(navigator.userAgent)
  if (webkit) await toPng(el, opts)
  const url = await toPng(el, opts)
  return (await fetch(url)).blob()
}

function fmtLap(seconds: number): string {
  const m = Math.floor(seconds / 60)
  return `${m}:${(seconds - m * 60).toFixed(3).padStart(6, '0')}`
}
function signed(v: number, decimals: number): string {
  if (!Number.isFinite(v)) return '–'
  return `${v > 0 ? '+' : v < 0 ? '−' : '±'}${Math.abs(v).toFixed(decimals)}`
}
function num(v: number, decimals: number): string {
  return Number.isFinite(v) ? v.toFixed(decimals) : '–'
}
function pair(a: number, b: number, decimals: number): string {
  return `${num(a, decimals)} → ${num(b, decimals)}`
}
function csvCell(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v
}
